import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './TryonPage.css';
import SeasonalCollections from '../components/SeasonalCollections';
import Layout from '../components/Layout';
import ShareModal from '../components/ShareModal';
import SaveToAlbumModal from '../components/SaveToAlbumModal';
import VideoGenerationModal from '../components/VideoGenerationModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TryonPage = () => {
  const navigate = useNavigate();
  const { user, logout, sessionToken, refreshUser } = useAuth();
  const [personImage, setPersonImage] = useState(null);
  const [garments, setGarments] = useState([]);
  const [filteredGarments, setFilteredGarments] = useState([]);
  const [selectedGarments, setSelectedGarments] = useState([]);
  const [resultImages, setResultImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  
  // Filters
  const [availableFilters, setAvailableFilters] = useState({
    brands: [],
    categories: [],
    genders: []
  });
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedGender, setSelectedGender] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [liveSearchQuery, setLiveSearchQuery] = useState('fashion clothing');
  
  // Share modal
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareImageUrl, setShareImageUrl] = useState('');
  
  // Save to album modal
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveImageData, setSaveImageData] = useState(null);
  
  // Video generation modal
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoImageData, setVideoImageData] = useState(null);

  const personInputRef = useRef(null);

  // Load garments and filters on mount
  useEffect(() => {
    loadGarments('fashion clothing'); // Use default query on mount
    loadFilters();
    loadHistory();
  }, []);

  const loadGarments = async (query = 'fashion clothing') => {
    try {
      setLoading(true);
      setLoadingMessage('Loading latest fashion items...');
      
      const response = await axios.get(`${API}/garments/search/live`, {
        params: {
          query: query,
          num_results: 30
        }
      });
      
      setGarments(response.data.garments || []);
      setFilteredGarments(response.data.garments || []);
      setLoadingMessage('');
    } catch (err) {
      console.error('Error loading garments:', err);
      setError('Failed to load garments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLiveSearch = async () => {
    if (liveSearchQuery.trim()) {
      await loadGarments(liveSearchQuery.trim());
    }
  };

  const loadFilters = async () => {
    try {
      const response = await axios.get(`${API}/garments/filters`);
      setAvailableFilters(response.data);
    } catch (err) {
      console.error('Error loading filters:', err);
    }
  };

  const loadHistory = async () => {
    try {
      const response = await axios.get(`${API}/tryon/history/all`);
      setHistory(response.data.history || []);
    } catch (err) {
      console.error('Error loading history:', err);
    }
  };

  const filterGarments = useCallback(() => {
    let filtered = [...garments];

    if (selectedBrand !== 'all') {
      filtered = filtered.filter(g => g.brand === selectedBrand);
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(g => g.category === selectedCategory);
    }

    if (selectedGender !== 'all') {
      filtered = filtered.filter(g => g.gender === selectedGender);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(g => 
        g.name.toLowerCase().includes(query) ||
        g.brand.toLowerCase().includes(query) ||
        g.description?.toLowerCase().includes(query)
      );
    }

    setFilteredGarments(filtered);
  }, [garments, selectedBrand, selectedCategory, selectedGender, searchQuery]);

  // Apply filters whenever dependencies change
  useEffect(() => {
    filterGarments();
  }, [filterGarments]);

  const handlePersonImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPersonImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const toggleGarmentSelection = (garment) => {
    const isSelected = selectedGarments.find(g => g.id === garment.id);
    
    if (isSelected) {
      setSelectedGarments(selectedGarments.filter(g => g.id !== garment.id));
    } else {
      setSelectedGarments([...selectedGarments, garment]);
    }
  };

  const generateTryon = async () => {
    if (!personImage) {
      setError('Please upload a photo of yourself');
      return;
    }

    if (selectedGarments.length === 0) {
      setError('Please select at least one garment');
      return;
    }

    // Check credits
    const creditsNeeded = selectedGarments.length;
    if (user.image_credits < creditsNeeded) {
      setError(`Not enough credits. You need ${creditsNeeded} credits but have ${user.image_credits}. Please purchase more credits.`);
      return;
    }

    setError(null);
    setLoading(true);
    setLoadingMessage('Preparing your virtual try-on...');

    try {
      const formData = new FormData();
      formData.append('person_image', personImage);
      formData.append('garment_ids', JSON.stringify(selectedGarments.map(g => g.id)));

      setLoadingMessage('Generating virtual try-on with FIT AND BOX AI (5-17 seconds)...');

      const response = await axios.post(`${API}/tryon/generate`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${sessionToken}`
        },
        timeout: 120000
      });

      setLoadingMessage('Processing results...');

      if (response.data.success) {
        setResultImages(response.data.images || []);
        setLoadingMessage(`Complete! Generated ${response.data.total_images} images`);
        await loadHistory();
        // Refresh user data to update credits
        await refreshUser();
      } else {
        setError('Failed to generate try-on');
      }
    } catch (err) {
      console.error('Error generating try-on:', err);
      if (err.response?.data?.detail) {
        setError(`Error: ${err.response.data.detail}`);
      } else if (err.code === 'ECONNABORTED') {
        setError('Request timed out. Please try again.');
      } else {
        setError('Failed to generate try-on. Please try again.');
      }
    } finally {
      setLoading(false);
      setTimeout(() => setLoadingMessage(''), 2000);
    }
  };

  const downloadImage = (imageData, index) => {
    if (!imageData || !imageData.result_image) return;

    try {
      // Create download link
      const link = document.createElement('a');
      link.href = imageData.result_image;
      link.download = `fittty-${imageData.garment_brand}-${imageData.garment_name}-${Date.now()}.png`;
      
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);
    } catch (err) {
      console.error('Download error:', err);
      alert('Download failed. Please try right-click and save image.');
    }
  };

  const downloadAllImages = () => {
    resultImages.forEach((img, idx) => {
      setTimeout(() => {
        downloadImage(img, idx);
      }, idx * 500); // Delay each download by 500ms
    });
  };

  const resetForm = () => {
    setPersonImage(null);
    setSelectedGarments([]);
    setResultImages([]);
    setError(null);
  };

  return (
    <Layout>
      <div className="tryon-page">
        {/* Seasonal Collections Section */}
      <div className="seasonal-section-wrapper">
        <SeasonalCollections
          sessionToken={sessionToken}
          onGenerate={(seasonalAlbum) => {
            // Reload user data to update credits
            window.location.reload();
          }}
        />
      </div>

      <header className="tryon-header">
        <h1 className="page-title">Virtual Try-On Studio</h1>
        <p className="page-subtitle">Upload your photo, select garments from our catalog, and see yourself in new outfits</p>
      </header>

      <div className="tryon-container">
        {/* Person Photo Upload Section */}
        <div className="person-section">
          <h3 className="section-title">Step 1: Upload Your Photo</h3>
          <div className="upload-card">
            <div
              className={`image-upload-area ${personImage ? 'has-image' : ''}`}
              onClick={() => !personImage && personInputRef.current?.click()}
            >
              {personImage ? (
                <div className="uploaded-image-container">
                  <img src={personImage} alt="Person" className="uploaded-image" />
                  <button
                    className="remove-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPersonImage(null);
                    }}
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div className="upload-placeholder">
                  <div className="upload-icon">📸</div>
                  <p>Click to upload your photo</p>
                  <span className="upload-hint">PNG, JPG up to 10MB</span>
                </div>
              )}
            </div>
            <input
              ref={personInputRef}
              type="file"
              accept="image/*"
              onChange={handlePersonImageUpload}
              style={{ display: 'none' }}
            />
          </div>
        </div>

        {/* Garment Catalog Section */}
        <div className="catalog-section">
          <h3 className="section-title">Step 2: Select Garments from Live Catalog</h3>
          
          {/* Live Search */}
          <div className="live-search-section">
            <div className="live-search-bar">
              <input
                type="text"
                placeholder="Search for fashion items (e.g., 'women dresses', 'men shirts', 'sneakers')..."
                value={liveSearchQuery}
                onChange={(e) => setLiveSearchQuery(e.target.value)}
                className="live-search-input"
                onKeyPress={(e) => e.key === 'Enter' && handleLiveSearch()}
              />
              <button 
                className="live-search-btn" 
                onClick={handleLiveSearch}
                disabled={loading}
              >
                {loading ? '🔄' : '🔍'} Search Live
              </button>
            </div>
            <p className="live-search-hint">
              🌟 Search real products from Google Shopping! Try: &quot;designer dresses&quot;, &quot;casual shirts&quot;, &quot;running shoes&quot;
            </p>
          </div>
          
          {/* Filters */}
          <div className="filters-bar">
            <div className="filter-group">
              <label>Brand:</label>
              <select value={selectedBrand} onChange={(e) => setSelectedBrand(e.target.value)}>
                <option value="all">All Brands</option>
                {availableFilters.brands.map(brand => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Category:</label>
              <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
                <option value="all">All Categories</option>
                {availableFilters.categories.map(cat => (
                  <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Gender:</label>
              <select value={selectedGender} onChange={(e) => setSelectedGender(e.target.value)}>
                <option value="all">All</option>
                {availableFilters.genders.map(gender => (
                  <option key={gender} value={gender}>{gender.charAt(0).toUpperCase() + gender.slice(1)}</option>
                ))}
              </select>
            </div>

            <div className="filter-group search-group">
              <input
                type="text"
                placeholder="Search garments..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>
          </div>

          {/* Selected Garments Display */}
          {selectedGarments.length > 0 && (
            <div className="selected-garments-bar">
              <span className="selected-count">{selectedGarments.length} garment{selectedGarments.length > 1 ? 's' : ''} selected</span>
              {selectedGarments.length > 1 && (
                <span className="info-note">⚡ First garment will be processed</span>
              )}
              <div className="selected-garments-list">
                {selectedGarments.map((g, idx) => (
                  <div key={g.id} className={`selected-garment-chip ${idx === 0 ? 'primary' : ''}`}>
                    {idx === 0 && <span className="first-badge">1st</span>}
                    {g.brand} {g.name}
                    <button onClick={() => toggleGarmentSelection(g)}>✕</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Garments Grid */}
          <div className="garments-grid">
            {filteredGarments.length === 0 ? (
              <div className="no-results">
                <p>No garments found matching your filters</p>
              </div>
            ) : (
              filteredGarments.map(garment => (
                <div
                  key={garment.id}
                  className={`garment-card ${selectedGarments.find(g => g.id === garment.id) ? 'selected' : ''}`}
                  onClick={() => toggleGarmentSelection(garment)}
                >
                  {selectedGarments.find(g => g.id === garment.id) && (
                    <div className="selected-badge">✓</div>
                  )}
                  <div className="garment-image-wrapper">
                    <img src={garment.image_url} alt={garment.name} className="garment-image" />
                  </div>
                  <div className="garment-info">
                    <div className="garment-brand">{garment.brand}</div>
                    <div className="garment-name">{garment.name}</div>
                    <div className="garment-price">${garment.price.toFixed(2)}</div>
                    <div className="garment-meta">
                      <span className="garment-category">{garment.category}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="actions-section">
          {error && <div className="error-message">{error}</div>}
          
          <div className="button-group">
            <button
              className="primary-btn"
              onClick={generateTryon}
              disabled={loading || !personImage || selectedGarments.length === 0}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Generating...
                </>
              ) : (
                'Generate Try-On'
              )}
            </button>
            <button className="secondary-btn" onClick={resetForm}>
              Reset
            </button>
          </div>

          {loadingMessage && (
            <div className="loading-status">
              <div className="status-bar"></div>
              <p>{loadingMessage}</p>
            </div>
          )}
        </div>

        {/* Result Section - Multiple Images */}
        {resultImages.length > 0 && (
          <div className="result-section">
            <div className="result-header">
              <h3 className="result-title">Your Virtual Try-Ons ({resultImages.length} images)</h3>
              {resultImages.length > 1 && (
                <button className="download-all-btn" onClick={downloadAllImages}>
                  💾 Download All ({resultImages.length})
                </button>
              )}
            </div>
            
            <div className="results-grid">
              {resultImages.map((imgData, index) => (
                <div key={index} className="result-card">
                  <div className="result-image-container">
                    <img src={imgData.result_image} alt={`Try-on ${index + 1}`} className="result-image" />
                  </div>
                  <div className="result-info">
                    <h4>{imgData.garment_brand}</h4>
                    <p className="garment-name">{imgData.garment_name}</p>
                    {imgData.garment_description && (
                      <p className="garment-description">{imgData.garment_description}</p>
                    )}
                    {(imgData.brand_website || imgData.product_link) && (
                      <a 
                        href={imgData.brand_website || imgData.product_link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="buy-now-btn"
                      >
                        🛒 Buy Now at {imgData.garment_brand}
                      </a>
                    )}
                    <div className="result-actions">
                      <button className="action-btn-primary" onClick={() => downloadImage(imgData, index)}>
                        ⬇️ Download
                      </button>
                      <button className="action-btn-primary" onClick={() => {
                        setShareImageUrl(imgData.result_image);
                        setShowShareModal(true);
                      }}>
                        📤 Share
                      </button>
                      <button className="action-btn-primary" onClick={() => {
                        setSaveImageData(imgData);
                        setShowSaveModal(true);
                      }}>
                        💾 Save to Album
                      </button>
                      <button 
                        className="action-btn-video" 
                        onClick={() => {
                          setVideoImageData(imgData.result_image);
                          setShowVideoModal(true);
                        }}
                        title="Generate 5-second video (2 credits)"
                      >
                        🎬 Video
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* History Section */}
        {history.length > 0 && (
          <div className="history-section">
            <h3 className="history-title">Recent Try-Ons</h3>
            <div className="history-grid">
              {history.map((item) => (
                <div key={item.id} className="history-item">
                  <img src={item.result_image} alt="History" />
                  <div className="history-info">
                    {item.garment_details && item.garment_details.length > 0 && (
                      <div className="history-garments">
                        {item.garment_details.map((g, idx) => (
                          <span key={idx}>{g.brand} {g.name}</span>
                        ))}
                      </div>
                    )}
                    <span className="history-date">
                      {new Date(item.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Share Modal */}
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          imageUrl={shareImageUrl}
          imageName="tryon"
        />
        
        {/* Save to Album Modal */}
        {showSaveModal && (
          <SaveToAlbumModal
            imageData={saveImageData}
            sessionToken={sessionToken}
            onClose={() => setShowSaveModal(false)}
            onSuccess={() => {
              alert('✅ Saved successfully!');
              setShowSaveModal(false);
            }}
          />
        )}
        
        {/* Video Generation Modal */}
        <VideoGenerationModal
          isOpen={showVideoModal}
          onClose={() => {
            setShowVideoModal(false);
            setVideoImageData(null);
          }}
          resultImage={videoImageData}
          onVideoGenerated={(data) => {
            console.log('Video generated:', data);
          }}
        />
      </div>
    </div>
    </Layout>
  );
};

export default TryonPage;